<script setup lang="ts">
import { ref } from 'vue'
import type { CallinglyCall } from '@/types/types'
import LoadingIcon from '@/components/LoadingIcon.vue'
import axios from '@/api'

const emit = defineEmits(['new-call'])

// State
const calls = ref<CallinglyCall[]>([])
const calls_loading = ref<boolean>(false)
const calls_page = ref<number>(1)
const hasNextPage = ref(false)

// Methods
const getCalls = (page = 1) => {
  if (page < 1 || (!hasNextPage.value && page > calls_page.value)) return

  calls_loading.value = true

  axios
    .get('/api/v1/calls?limit=5', {
      params: { page },
    })
    .then((response) => {
      calls.value = response.data.data
      calls_page.value = page
      hasNextPage.value = !!response.data.links?.next
    })
    .finally(() => {
      calls_loading.value = false
    })
}

// Cell base = Bootstrap 3 .table td metrics
const td = 'p-[8px] leading-[1.42857143] align-top border-t border-[#ddd]'
// Row base = .table-striped zebra + .table-hover highlight
const tr = 'odd:bg-[#f9f9f9] hover:bg-[#f5f5f5]'

// Load initial data
getCalls()
</script>

<template>
  <h3 class="text-center mt-0 mb-[20px]">Call History</h3>
  <table class="w-full max-w-full border-collapse mb-0 bg-white">
    <tbody>
      <tr v-if="calls_loading" :class="tr">
        <td colspan="7" :class="td">
          <p class="text-center"><LoadingIcon /></p>
        </td>
      </tr>
      <tr v-else-if="!calls.length" :class="tr">
        <td colspan="7" :class="td">
          <p class="text-center">You don't have any calls!</p>
        </td>
      </tr>
      <tr v-else v-for="call in calls" :key="call.id" class="h-[72px]" :class="tr">
        <td class="text-left relative" :class="td">
          <i class="material-icons text-[16px]! mt-[2px]">{{
            call?.direction == 'outbound' ? 'call_made' : 'call_received'
          }}</i>
          <audio
            v-if="call?.recording_url"
            controls
            class="h-[29px] w-[143px] absolute left-[10px] bottom-[8px]"
          >
            <source :src="call?.recording_url" type="audio/mpeg" />
          </audio>
        </td>
        <td :class="td">
          {{ call.time_formatted }}
          <div v-if="call.lead_retry" class="text-[9px] pt-[3px]">
            Followup #{{ call.lead_retry }}
          </div>
        </td>
        <td :class="td">
          <div v-if="call.status == 'missed'">Missed</div>
          <div v-else-if="call.status == 'busy'">Busy</div>
          <div v-else-if="call.status == 'offline'">Offline</div>
          <div v-else-if="call.status == 'in-progress'">In Progress</div>
          <div v-else-if="call.status == 'ringing'">Ringing</div>
          <div v-else-if="!call.status" />
          <div v-else-if="call.seconds == 0">No Answer</div>
          <div v-else>
            {{ call.duration }}
          </div>

          <div v-if="call.retry" class="text-[9px] pt-[3px]">Retry #{{ call.retry }}</div>

          <div v-if="call.status == 'missed'">No agents answered</div>
          <div v-else-if="call.status == 'offline'">Outside availability hours</div>
          <div v-else-if="call.status == 'busy'">All agents busy</div>
          <div v-if="call.user">
            {{ call.user.name }}
          </div>
        </td>
        <td :class="td">
          <div v-if="call.lead">
            <a href="#" @click="emit('new-call', call.lead)">{{ call.lead.label }}</a
            ><br />
            <span v-if="call.lead.fname">{{ call.lead.phone_number_formatted }}</span>
          </div>
          <div v-else>
            {{ call.phone_number_formatted }}
          </div>
        </td>
      </tr>
    </tbody>
  </table>
  <ul
    class="my-0 text-center list-none flow-root px-[20px] pt-[14px] absolute right-0 left-0 bottom-[calc(65px+env(safe-area-inset-bottom,0px))]"
  >
    <li class="inline">
      <a
        class="inline-block float-left px-[14px] py-[5px] rounded-[4px] bg-white border border-[#ddd] hover:no-underline"
        :class="
          calls_page < 2
            ? 'text-[#777] cursor-not-allowed pointer-events-none'
            : 'hover:bg-[#eee] focus:bg-[#eee]'
        "
        href="#"
        rel="previous"
        @click="getCalls(calls_page - 1)"
        >Previous</a
      >
    </li>
    <li class="inline">
      <a
        class="inline-block float-right px-[14px] py-[5px] rounded-[4px] bg-white border border-[#ddd] hover:no-underline"
        :class="
          !hasNextPage
            ? 'text-[#777] cursor-not-allowed pointer-events-none'
            : 'hover:bg-[#eee] focus:bg-[#eee]'
        "
        href="#"
        rel="next"
        @click="getCalls(calls_page + 1)"
        >Next</a
      >
    </li>
  </ul>
</template>
